import { Component } from '@theme/component';

/**
 * Trendyol reviews: the section renders the first reviews in Liquid and keeps the whole list in a
 * JSON script. "Daha fazla göster" adds the next batch from that list using the row template, writing
 * every review field with textContent so customer text is never parsed as HTML.
 *
 * @typedef {object} TrendyolReviewsRefs
 * @property {HTMLUListElement} list - The review list
 * @property {HTMLScriptElement} [data] - JSON array of all reviews (newest first)
 * @property {HTMLTemplateElement} [template] - One review row
 * @property {HTMLButtonElement} [more] - The "show more" button
 *
 * @extends {Component<TrendyolReviewsRefs>}
 */
class TrendyolReviewsComponent extends Component {
  requiredRefs = ['list'];

  /** @type {Array<{a?: string, b?: string, r?: string, y: string, t: string}> | undefined} */
  #reviews;

  #shown = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#shown = this.refs.list.children.length;
  }

  showMore() {
    const { data, template, more, list } = this.refs;
    if (!data || !template || !more) return;

    this.#reviews ??= JSON.parse(data.textContent || '[]');
    const reviews = this.#reviews ?? [];
    const step = Number(this.dataset.step) || 10;
    const months = (more.dataset.months || '').split(',');
    const fragment = document.createDocumentFragment();

    for (const review of reviews.slice(this.#shown, this.#shown + step)) {
      const row = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const text = row.querySelector('.ty-review__text');
      const name = row.querySelector('[data-field="name"]');
      const detail = row.querySelector('[data-field="detail"]');
      const date = row.querySelector('[data-field="date"]');

      if (text) text.textContent = review.y;
      if (name) name.textContent = review.a || more.dataset.buyer || '';
      if (detail) {
        const size = (more.dataset.size || '').replace('[size]', review.b || '');
        detail.textContent = review.r ? `${size} · ${review.r}` : size;
      }
      if (date) {
        const [year, month, day] = review.t.split('-');
        date.textContent = `${Number(day)} ${months[Number(month) - 1] || ''} ${year}`;
      }
      fragment.append(row);
    }

    list.append(fragment);
    this.#shown = Math.min(this.#shown + step, reviews.length);

    const left = reviews.length - this.#shown;
    if (left > 0) {
      more.textContent = (more.dataset.label || '').replace('[count]', String(left));
    } else {
      more.hidden = true;
    }
  }
}

if (!customElements.get('trendyol-reviews')) {
  customElements.define('trendyol-reviews', TrendyolReviewsComponent);
}
